/**
 * Loads product-card and quick-view behavior only where it is needed.
 *
 * Product pages initialize the product controller immediately. Product-card helpers
 * load only when matching markup exists. On mobile, the quick-view stack preloads
 * when the first quick-view control enters the viewport. Desktop preloads on intent.
 */

const config = document.getElementById('ProductEcosystemLoader')?.dataset

if (config) {
  const MOBILE_QUERY = '(max-width: 719px)'
  const QUICK_VIEW_SELECTOR = '[data-quick-shop-trigger="quick-view"], [data-quick-view-trigger]'
  const scriptPromises = new Map()
  const stylePromises = new Map()
  const mobileMedia = window.matchMedia(MOBILE_QUERY)

  let observedQuickViewControls = new WeakSet()
  let quickViewPromise
  let quickViewReady = false
  let viewportObserver
  let mutationObserver

  function loadScript(src) {
    if (!src) return Promise.resolve()
    if (scriptPromises.has(src)) return scriptPromises.get(src)

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.type = 'module'
      script.src = src
      script.crossOrigin = 'anonymous'
      script.addEventListener('load', resolve, { once: true })
      script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true })
      document.head.appendChild(script)
    }).catch((error) => {
      scriptPromises.delete(src)
      throw error
    })

    scriptPromises.set(src, promise)
    return promise
  }

  function loadStyle(href) {
    if (!href) return Promise.resolve()
    if (stylePromises.has(href)) return stylePromises.get(href)

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.addEventListener('load', resolve, { once: true })
      link.addEventListener('error', () => reject(new Error(`Failed to load ${href}`)), { once: true })
      document.head.appendChild(link)
    }).catch((error) => {
      stylePromises.delete(href)
      throw error
    })

    stylePromises.set(href, promise)
    return promise
  }

  function loadProductController() {
    return Promise.all([loadStyle(config.productStyleSrc), loadScript(config.productSrc)])
  }

  function loadProductItem() {
    return loadScript(config.productItemSrc)
  }

  function mountQuickViewModal() {
    if (document.querySelector('[data-quick-view-modal]')) return

    const template = document.querySelector('[data-quick-view-modal-template]')
    if (template instanceof HTMLTemplateElement) {
      document.body.appendChild(template.content.cloneNode(true))
    }
  }

  function stopMobileObservation() {
    viewportObserver?.disconnect()
    mutationObserver?.disconnect()
    viewportObserver = undefined
    mutationObserver = undefined
    observedQuickViewControls = new WeakSet()
  }

  function loadQuickView() {
    if (quickViewPromise) return quickViewPromise

    stopMobileObservation()
    mountQuickViewModal()

    quickViewPromise = Promise.all([loadProductController(), loadProductItem()])
      .then(() => loadScript(config.quickViewSrc))
      .then(() => {
        quickViewReady = true
      })
      .catch((error) => {
        quickViewPromise = undefined
        throw error
      })

    return quickViewPromise
  }

  function quickViewControl(target) {
    return target instanceof Element ? target.closest(QUICK_VIEW_SELECTOR) : null
  }

  function productUrl(control) {
    return control?.dataset.productUrl || control?.getAttribute('href') || ''
  }

  function observeQuickViewControls(root = document) {
    if (!viewportObserver || quickViewPromise) return

    const controls = []
    if (root.matches?.(QUICK_VIEW_SELECTOR)) controls.push(root)
    root.querySelectorAll?.(QUICK_VIEW_SELECTOR).forEach((control) => controls.push(control))

    controls.forEach((control) => {
      if (observedQuickViewControls.has(control)) return
      observedQuickViewControls.add(control)
      viewportObserver.observe(control)
    })
  }

  function startMobileObservation() {
    if (!mobileMedia.matches || quickViewPromise || config.quickViewEnabled !== 'true') return
    if (!('IntersectionObserver' in window)) return

    viewportObserver ||= new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      loadQuickView().catch((error) => console.error('[product-loader] quick view preload failed', error))
    })

    observeQuickViewControls()

    if (!mutationObserver && document.body) {
      mutationObserver = new MutationObserver((records) => {
        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (node instanceof Element) observeQuickViewControls(node)
          })
        })
      })
      mutationObserver.observe(document.body, { childList: true, subtree: true })
    }
  }

  function loadConditionalFeatures(root = document) {
    const hasProductCards = root.matches?.('.product-item') || root.querySelector?.('.product-item')
    const hasQuickShop =
      root.matches?.('[data-quick-shop-trigger]') || root.querySelector?.('[data-quick-shop-trigger]')
    const hasRecommendations =
      root.matches?.('[data-section-type="recommended-products"]') ||
      root.querySelector?.('[data-section-type="recommended-products"]')
    const hasCrossSells = root.matches?.('[data-cross-sells]') || root.querySelector?.('[data-cross-sells]')

    if (hasProductCards || hasQuickShop || hasRecommendations) {
      loadProductItem().catch((error) => console.error('[product-loader] product cards failed', error))
    }

    if (hasRecommendations) {
      loadProductItem()
        .then(() => loadScript(config.productRecommendationsSrc))
        .catch((error) => console.error('[product-loader] recommendations failed', error))
    }

    if (hasCrossSells) {
      loadScript(config.crossSellsSrc).catch((error) => console.error('[product-loader] cross-sells failed', error))
    }
  }

  if (config.productPage === 'true') {
    loadProductController().catch((error) => console.error('[product-loader] product page failed', error))
  }

  loadConditionalFeatures()
  startMobileObservation()

  document.addEventListener(
    'pointerover',
    (event) => {
      if (mobileMedia.matches || !quickViewControl(event.target)) return
      loadQuickView().catch((error) => console.error('[product-loader] quick view preload failed', error))
    },
    { capture: true, passive: true }
  )

  document.addEventListener(
    'focusin',
    (event) => {
      if (!quickViewControl(event.target)) return
      loadQuickView().catch((error) => console.error('[product-loader] quick view preload failed', error))
    },
    true
  )

  document.addEventListener(
    'click',
    async (event) => {
      const control = quickViewControl(event.target)
      if (!control || quickViewReady) return

      const url = productUrl(control)
      if (!url) return

      event.preventDefault()
      event.stopImmediatePropagation()

      try {
        await loadQuickView()
        document.dispatchEvent(new CustomEvent('quick-view:open', { detail: { productUrl: url } }))
      } catch (error) {
        console.error('[product-loader] quick view failed', error)
        window.location.assign(url)
      }
    },
    true
  )

  document.addEventListener('shopify:section:load', (event) => {
    loadConditionalFeatures(event.target)
    if (mobileMedia.matches) observeQuickViewControls(event.target)
  })

  mobileMedia.addEventListener('change', (event) => {
    if (event.matches) startMobileObservation()
    else stopMobileObservation()
  })
}
